import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Video } from '@/mocks/videos';
import { formatDuration, formatViews } from '@/utils/format';

interface HeroCarouselProps {
  videos: Video[];
}

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (videos.length <= 1) return;
    timer.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % videos.length);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [videos.length]);

  if (videos.length === 0) return null;

  const go = (i: number) => {
    setIndex((i + videos.length) % videos.length);
  };

  const current = videos[index];

  return (
    <section className="relative w-full h-[380px] md:h-[560px] overflow-hidden">
      {videos.map((v, i) => (
        <div
          key={v.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}
        >
          <img src={v.cover} alt={v.title} title={`${v.title} - 靠逼视频`} className="w-full h-full object-cover object-top" />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-background-950/95 via-background-950/70 to-background-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>

      <div className="relative h-full w-full px-4 md:px-8 flex items-center">
        <div className="w-full max-w-4xl">
          <div className="flex items-center gap-3 mb-3 md:mb-5">
            <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
              编辑精选
            </span>
            <span className="text-xs text-foreground-700">
              <i className="ri-fire-line text-accent-400 mr-1"></i>
              {formatViews(current.views)} 次观看
            </span>
          </div>
          <h1 className="font-heading text-2xl md:text-5xl font-extrabold text-foreground-950 leading-tight max-w-3xl">
            {current.title}
          </h1>
          <p className="mt-3 md:mt-5 text-sm md:text-base text-foreground-700 leading-relaxed max-w-2xl line-clamp-2 md:line-clamp-2">
            {current.description}
          </p>
          <div className="mt-5 md:mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/detail/${current.id}`)}
              className="h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </button>
            <button
              type="button"
              onClick={() => navigate(`/category/${current.categoryId}`)}
              className="h-11 px-6 rounded-md bg-background-100/80 hover:bg-background-200 border border-background-300 text-foreground-950 text-sm font-medium flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-apps-2-line"></i>
              更多同类
            </button>
            <span className="text-xs text-foreground-600 flex items-center gap-2">
              <i className="ri-time-line"></i>
              {formatDuration(current.duration)}
            </span>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-950/50 hover:bg-primary-500 text-foreground-950 text-xl transition-colors cursor-pointer"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-950/50 hover:bg-primary-500 text-foreground-950 text-xl transition-colors cursor-pointer"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line"></i>
      </button>

      <div className="absolute bottom-5 md:bottom-8 left-4 md:left-8 flex items-center gap-2">
        {videos.map((v, i) => (
          <button
            key={v.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第 ${i + 1} 个`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-primary-500' : 'w-3 bg-background-800/70 hover:bg-background-900'
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}